import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getContentModelUseFeaturedImage } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { H4 } from 'UIComponents/elements/headings';
import UITourStep from 'ui-shepherd-react/tour/UITourStep';
import UITourBackButton from 'ui-shepherd-react/button/UITourBackButton';
import UITourNextButton from 'ui-shepherd-react/button/UITourNextButton';
import UITourFinishButton from 'ui-shepherd-react/button/UITourFinishButton';
import TourStepCount from 'ContentEditorUI/components/shared/TourStepCount';
import { BLOG_PLG_ONBOARDING_TOUR, useTourCallbacks } from './OnboardingTour';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useSmoothScrollToSetting = (targetSelector, attachTo) => {
  const timeoutRef = useRef(undefined);
  const scrollTargetRef = useRef(null);
  const [pulserProps, setPulserProps] = useState({
    attachTo: '',
    enableBorder: false,
    enableOverlay: false
  });
  const getScrollTargetElem = useCallback(() => {
    if (scrollTargetRef.current && scrollTargetRef.current.parentNode) {
      return scrollTargetRef.current;
    }
    const scrollTarget = document.querySelector(targetSelector);
    if (!scrollTarget) {
      return null;
    }
    scrollTargetRef.current = scrollTarget;
    return scrollTarget;
  }, [targetSelector]);
  const scrollToTarget = useCallback(() => {
    const scrollTarget = getScrollTargetElem();
    if (!scrollTarget) {
      return;
    }
    window.requestAnimationFrame(() => {
      scrollTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }, [getScrollTargetElem]);
  useEffect(() => {
    const scrollTarget = getScrollTargetElem();
    if (!scrollTarget) {
      return () => {};
    }
    const observerOptions = {
      root: document.querySelector('[data-shepherd-id="blog-settings-modal-DialogBody"]'),
      threshold: 0.9
    };
    const intersectionObserver = new IntersectionObserver(entries => {
      const [entry] = entries;
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setPulserProps({
          attachTo: attachTo || '',
          enableBorder: entry.isIntersecting,
          enableOverlay: entry.isIntersecting
        });
      }, 15);
    }, observerOptions);
    intersectionObserver.observe(scrollTarget);
    return () => {
      intersectionObserver.unobserve(scrollTarget);
      clearTimeout(timeoutRef.current);
    };
  }, [attachTo, getScrollTargetElem]);
  return {
    pulserProps,
    scrollToTarget
  };
};
const OnboardingTourNextButton = ({
  children
}) => {
  const {
    onBeforeNextStep
  } = useTourCallbacks();
  return /*#__PURE__*/_jsx(UITourNextButton, {
    beforeNext: onBeforeNextStep,
    use: "primary",
    children: children
  });
};
export const OnboardingUITourStepPostName = ({
  children
}) => {
  const stepKey = 'postName';
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: stepKey,
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.postName.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.postName.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "plgOnboardingTour.step.postName.body_2"
            })
          })
        })]
      }),
      footer: /*#__PURE__*/_jsx(OnboardingTourNextButton, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.postName.button.next"
        })
      })
    },
    pulser: true,
    children: children
  });
};
export const OnboardingUITourStepMetaDescription = ({
  children
}) => {
  const targetSelector = '[data-shepherd-id="post-settings-meta-description"]';
  const attachTo = "textarea[name='meta_description']";
  const {
    pulserProps,
    scrollToTarget
  } = useSmoothScrollToSetting(targetSelector, attachTo);
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: "metaDescription",
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.metaDescription.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.metaDescription.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "plgOnboardingTour.step.metaDescription.body_2"
            })
          })
        })]
      }),
      footer: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UITourBackButton, {}), /*#__PURE__*/_jsx(OnboardingTourNextButton, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.metaDescription.button.next"
          })
        })]
      })
    },
    pulser: pulserProps,
    width: 400,
    beforeStart: scrollToTarget,
    children: children
  });
};
export const OnboardingUITourStepAuthor = ({
  children
}) => {
  const targetSelector = '[data-shepherd-id="post-settings-author"]';
  const {
    pulserProps,
    scrollToTarget
  } = useSmoothScrollToSetting(targetSelector);
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: "author",
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.author.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.author.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "plgOnboardingTour.step.author.body_2"
            })
          })
        })]
      }),
      footer: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UITourBackButton, {}), /*#__PURE__*/_jsx(OnboardingTourNextButton, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.author.button.next"
          })
        })]
      })
    },
    pulser: pulserProps,
    width: 440,
    beforeStart: scrollToTarget,
    children: children
  });
};
export const OnboardingUITourStepFeaturedImage = ({
  children
}) => {
  const isFeaturedImageEnabled = useSelector(getContentModelUseFeaturedImage);
  const targetSelector = '[data-shepherd-id="post-settings-featured-image"]';
  const attachTo = isFeaturedImageEnabled ? `${targetSelector} .image-upload-zone` :
  // eslint-disable-next-line hubspot-dev/no-private-classes
  '.featured-image-section .private-multicolumn__group';
  const {
    pulserProps,
    scrollToTarget
  } = useSmoothScrollToSetting(`${targetSelector} .featured-image-section`, attachTo);
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: "featuredImage",
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.featuredImage.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.featuredImage.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "plgOnboardingTour.step.featuredImage.body_2"
            })
          })
        })]
      }),
      footer: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UITourBackButton, {}), /*#__PURE__*/_jsx(OnboardingTourNextButton, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.featuredImage.button.next"
          })
        })]
      })
    },
    pulser: pulserProps,
    width: 400,
    beforeStart: scrollToTarget,
    children: children
  });
};
export const OnboardingUITourStepTemplate = ({
  children
}) => {
  const targetSelector = '[data-shepherd-id="post-settings-template"]';
  const {
    pulserProps,
    scrollToTarget
  } = useSmoothScrollToSetting(targetSelector);
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: "template",
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.template.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "plgOnboardingTour.step.template.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.template.body_2"
          })
        })]
      }),
      footer: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UITourBackButton, {}), /*#__PURE__*/_jsx(OnboardingTourNextButton, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.template.button.next"
          })
        })]
      })
    },
    pulser: pulserProps,
    width: 400,
    beforeStart: scrollToTarget,
    children: children
  });
};
export const OnboardingUITourStepPublish = ({
  children
}) => {
  const {
    onBeforeNextStep
  } = useTourCallbacks();
  return /*#__PURE__*/_jsx(UITourStep, {
    stepKey: "publish",
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    showCloseButton: true,
    content: {
      header: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.step.publish.header"
        })
      }),
      body: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(TourStepCount, {
          tourKey: BLOG_PLG_ONBOARDING_TOUR
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.publish.body"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "plgOnboardingTour.step.publish.body_2"
            })
          })
        })]
      }),
      footer: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UITourBackButton, {}), /*#__PURE__*/_jsx(UITourFinishButton, {
          beforeFinish: onBeforeNextStep,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "plgOnboardingTour.step.publish.button.done"
          })
        })]
      })
    },
    pulser: true,
    width: 380,
    children: children
  });
};